import {
  type Account,
  AccountPlanType,
  AccountType,
} from '../../components/Search/SearchNav/types';
import {
  UsageType,
  useBillingPeriodSubscriptionsQuery,
} from '../../generated/graphql';
import {
  getWbagentSubBillingPeriod,
  useAccountPrivileges,
} from '../accounts/pricing';
import {useRampFlagEnableWbAgentUsageCharts} from '../rampFeatureFlags/rampFeatureFlags';
import {
  getIntervalMarkersFromCurrentCalendarMonth,
  getIntervalMarkersFromLimitDateInterval,
  getIntervalMarkersFromSubscription,
  useGetUsageThisBillingPeriod,
} from './utils';

function isWbAgentBillingPlanEligible(account: Account): boolean {
  return (
    account.accountPlanType === AccountPlanType.Pro ||
    account.accountPlanType === AccountPlanType.Enterprise
  );
}

/** Call only when `orgName` is known (e.g. from the URL or account selector). */
export function useIsWbAgentBillingUsageVisible(orgName: string): {
  visible: boolean;
  loading: boolean;
} {
  const {enabled, loading} = useRampFlagEnableWbAgentUsageCharts(orgName);
  return {
    visible: !loading && enabled,
    loading,
  };
}

function useWbAgentBillingUsageVisibility(orgName: string | null): {
  visible: boolean;
  loading: boolean;
} {
  const hasOrg = orgName != null;

  // When there is no org, pass null so useOrgNameRampFlag skips the query
  // (its skip condition is `orgName == null`). Treat the flag as false.
  const {enabled, loading: rampLoading} = useRampFlagEnableWbAgentUsageCharts(
    (hasOrg ? orgName : null) as string
  );

  if (!hasOrg) {
    return {visible: false, loading: false};
  }

  return {
    visible: !rampLoading && enabled,
    loading: rampLoading,
  };
}

/**
 * Hook to get WB Agent usage details for an account.
 *
 * NOTE: This hook is for multi-tenant (SaaS) deployments only. Do NOT use in
 * single-tenant (server) deployments. For server deployments, use the hooks in
 * ServerCurrentBillingPeriodUsageSection.tsx.
 */
export function useGetWbagentUsageDetails(selectedAccount: Account | null) {
  const orgName =
    selectedAccount?.accountType === AccountType.Personal
      ? null
      : (selectedAccount?.name ?? null);
  const {visible: isRampVisible, loading: visibilityLoading} =
    useWbAgentBillingUsageVisibility(orgName);
  const isVisible =
    isRampVisible &&
    selectedAccount != null &&
    isWbAgentBillingPlanEligible(selectedAccount);
  const skip = selectedAccount?.name == null || visibilityLoading || !isVisible;

  const privilegeResults = useAccountPrivileges(selectedAccount);
  const {privileges} = privilegeResults;
  const {wbagentLimits} = privileges;

  const billingPeriodResults = useBillingPeriodSubscriptionsQuery({
    variables: {orgName: skip ? '' : selectedAccount!.name},
    skip,
  });

  const organization = billingPeriodResults.data?.organization ?? undefined;

  // Usage totals come from internal cost records (GraphQL usageTotal), not Stripe/Orb billing.
  const isUsagePaid = false;

  const usagePeriod = selectedAccount?.isEnterprise
    ? getIntervalMarkersFromLimitDateInterval(
        privileges.wbagentLimitDateInterval
      )
    : (getIntervalMarkersFromSubscription(
        getWbagentSubBillingPeriod(
          selectedAccount?.accountPlanType,
          organization
        )
      ) ?? getIntervalMarkersFromCurrentCalendarMonth());

  const usageResults = useGetUsageThisBillingPeriod(
    selectedAccount,
    UsageType.WbAgentCost,
    usagePeriod,
    skip
  );

  const loading =
    visibilityLoading ||
    billingPeriodResults.loading ||
    privilegeResults.loading ||
    usageResults.loading;
  const error =
    billingPeriodResults.error ?? privilegeResults.error ?? usageResults.error;
  const [billingPeriodStart, billingPeriodEnd] = usagePeriod;
  const {usage} = usageResults;

  return {
    isUsagePaid,
    wbagentLimits,
    billingPeriodStart,
    billingPeriodEnd,
    usage,
    loading,
    error,
    isVisible,
    visibilityLoading,
  };
}
